/* eslint-disable no-case-declarations */
import React, { useEffect, useMemo, useState, useRef } from 'react';
import { Button, Col, FormGroup, Input, Label, Row, Table, UncontrolledTooltip } from 'reactstrap';
import PropTypes from 'prop-types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { cloneDeep, debounce, sumBy } from 'lodash-es';
import { useSelector } from 'react-redux';
import { useIsComponentMounted } from '../../../hooks';
import { formatChannelText } from '../../../utilities/textsFormatters';
import {
  preformatFloat,
  locale,
  allVisibleArePaid,
  formatCurrencyForLocale,
} from '../../../utilities/invoiceUtils';
import Modal from '../../../ui/Modal';

const blockEl = 'invoices__add-charges-modal';
const AddChargesModalByInvoice = ({
  isBulkAction,
  isOpen,
  onClose,
  handleAddChargesByInvoice,
  handleBulkActions,
  currencySymbol,
  paymentProcessorType,
  invoices: originalInvoices,
  setIndex,
  variant,
  hideAdditionalCode,
}) => {
  const prevCharges = { prevInvoices: [{ invoice: {
    shipping: '',
    tax: '',
    other: '',
    insurance: '',
    invoiceID: '',
  } }] };
  const [invoices, setInvoices] = useState([]);
  const [invoiceIndex, setInvoiceIndex] = useState(0);
  const [isBulkActionStart, setIsBulkActionStart] = useState(false);
  const [bulkActionIndex, setBulkActionIndex] = useState(0);
  const [prevBulkInvoices, setPrevBulkInvoices] = useState(prevCharges);
  const [isSavingCharges, setIsSavingCharges] = useState(false);
  const [additionalCharges, setAdditionalCharges] = useState(parseFloat('0.00').toFixed(2));
  const [addInsurance, setAddInsurance] = useState(parseFloat('0.00').toFixed(2));
  const [addTax, setAddTax] = useState(parseFloat('0.00').toFixed(2));
  const [addShipping, setAddShipping] = useState(parseFloat('0.00').toFixed(2));
  const [addOther, setAddOther] = useState(parseFloat('0.00').toFixed(2));
  const [usPayrixUser, setUsPayrixUser] = useState(false);
  const [isPaidOrPending, setIsPaidOrPending] = useState(false);
  const [isPaid, setIsPaid] = useState(false);
  const [isLotAdditionalVariant, setIsLotAdditionalVariant] = useState(variant === 'lot-additional');
  const [isLotVariant, setIsLotVariant] = useState(variant === 'lot');
  const [textAdditionalPrefix, setTextAdditionalPrefix] = useState('');
  const previousBulkData = useRef(null);
  const isComponentMounted = useIsComponentMounted();
  const headingTotalText = 'Total Additional Charges';
  const currentInvoice = useMemo(() => invoices[invoiceIndex], [invoices, invoiceIndex]);
  const { paymentProcessingEnabled } = useSelector(state => state.house);
  const [hidePreviousLink, setHidePreviousLink] = useState(false);

  useEffect(() => {
    if (isComponentMounted.current) {
      if (typeof originalInvoices === 'undefined'
      || !Array.isArray(originalInvoices)
      || !originalInvoices.length) {
        return;
      }
      setInvoices(cloneDeep(originalInvoices));
      if (prevBulkInvoices && bulkActionIndex <= invoiceIndex && isBulkActionStart) {
        setIsBulkActionStart(false);
        return;
      }

      if (setIndex > 0) {
        setInvoiceIndex(setIndex);
        setBulkActionIndex(setIndex);
      }
      // reset indexes when going back into bulk mode
      if (bulkActionIndex >= originalInvoices.length) {
        setInvoiceIndex(0);
        setBulkActionIndex(0);
      }

      if (isBulkAction && (prevBulkInvoices.prevInvoices.length < originalInvoices.length)) {
        const tax = parseFloat(preformatFloat(originalInvoices[bulkActionIndex].charges.tax));
        const shipping = parseFloat(preformatFloat(originalInvoices[bulkActionIndex].charges.shipping));
        const insurance = parseFloat(preformatFloat(originalInvoices[bulkActionIndex].charges.insurance));
        const other = parseFloat(preformatFloat(originalInvoices[bulkActionIndex].charges.other));
        setAddTax(tax);
        setAddShipping(shipping);
        setAddInsurance(insurance);
        setAddOther(other);
        const onLoadAdditionalTotal = parseFloat(preformatFloat(originalInvoices[bulkActionIndex].charges.tax)) + parseFloat(preformatFloat(originalInvoices[bulkActionIndex].charges.shipping)) + parseFloat(preformatFloat(originalInvoices[bulkActionIndex].charges.insurance)) + parseFloat(preformatFloat(originalInvoices[bulkActionIndex].charges.other));
        setAdditionalCharges(onLoadAdditionalTotal.toLocaleString(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
        setUsPayrixUser(paymentProcessingEnabled && paymentProcessorType === 'p' && originalInvoices[bulkActionIndex].country === 'US');
        setIsPaidOrPending(originalInvoices[bulkActionIndex].isPaid || originalInvoices[bulkActionIndex].isPendingPayment);
      } else if (isBulkAction && prevBulkInvoices.prevInvoices.length > originalInvoices.length && originalInvoices.length != invoiceIndex + 2) {
        const tax = parseFloat(preformatFloat(prevBulkInvoices.prevInvoices[bulkActionIndex + 1].invoice.tax));
        const shipping = parseFloat(preformatFloat(prevBulkInvoices.prevInvoices[bulkActionIndex + 1].invoice.shipping));
        const insurance = parseFloat(preformatFloat(prevBulkInvoices.prevInvoices[bulkActionIndex + 1].invoice.insurance));
        const other = parseFloat(preformatFloat(prevBulkInvoices.prevInvoices[bulkActionIndex + 1].other));
        setAddTax(tax);
        setAddShipping(shipping);
        setAddInsurance(insurance);
        setAddOther(other);
        const onLoadAdditionalTotal = parseFloat(preformatFloat(prevBulkInvoices.prevInvoices[bulkActionIndex + 1].invoice.tax)) + parseFloat(preformatFloat(prevBulkInvoices.prevInvoices[bulkActionIndex + 1].invoice.shipping)) + parseFloat(preformatFloat(prevBulkInvoices.prevInvoices[bulkActionIndex + 1].invoice.insurance)) + parseFloat(preformatFloat(prevBulkInvoices.prevInvoices[bulkActionIndex + 1].invoice.other));
        setAdditionalCharges(onLoadAdditionalTotal.toLocaleString(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
        setUsPayrixUser(paymentProcessingEnabled && paymentProcessorType === 'p' && prevBulkInvoices.prevInvoices[bulkActionIndex + 1].country === 'US');
        setIsPaidOrPending(prevBulkInvoices.prevInvoices[bulkActionIndex + 1].invoice.isPaid || prevBulkInvoices.prevInvoices[bulkActionIndex + 1].invoice.isPendingPayment);
      } else {
        originalInvoices.map((invoice) => {
          const tax = parseFloat(preformatFloat(invoice.charges.tax));
          const shipping = parseFloat(preformatFloat(invoice.charges.shipping));
          const insurance = parseFloat(preformatFloat(invoice.charges.insurance));
          const other = parseFloat(preformatFloat(invoice.charges.other));
          setAddTax(tax);
          setAddShipping(shipping);
          setAddInsurance(insurance);
          setAddOther(other);
          const onLoadAdditionalTotal = tax + shipping + insurance + other;
          setAdditionalCharges(onLoadAdditionalTotal.toLocaleString(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
          setUsPayrixUser(paymentProcessingEnabled && paymentProcessorType === 'p' && invoice.country === 'US');
          setIsPaidOrPending(invoice.isPaid || invoice.isPendingPayment);
        });
      }
    }
  }, [
    isBulkAction,
    bulkActionIndex,
    originalInvoices
  ]);

  useEffect(() => {
    if (invoices.length > 0) {
      invoices.forEach(invoice => {
        if (invoice.showRestrictedModal) setHidePreviousLink(true);
      });
    }
  }, [invoices]);

  const clearInputField = (e) => {
    if (e.currentTarget.value === '0.00' || e.currentTarget.value === '0') {
      e.currentTarget.value = '';
    }
    e.currentTarget.classList.remove('zero-value-color');
  };

  const setFieldClasses = (value, paid) => {
    let returnString = '';
    if (value === '0.00') {
      returnString += returnString.length > 0 ? ' zero-value-color' : 'zero-value-color';
    }
    if (paid && isLotVariant) {
      returnString += returnString.length > 0 ? ' disabled-cursor' : 'disabled-cursor';
    }

    return returnString;
  };

  const handlePreviousClickEvent = () => {
    previousBulkData.current = prevBulkInvoices;
    if (previousBulkData.current) {
      setIsBulkActionStart(true);
      setAddTax(prevBulkInvoices.prevInvoices[bulkActionIndex].invoice.tax);
      setAddShipping(prevBulkInvoices.prevInvoices[bulkActionIndex].invoice.shipping);
      setAddInsurance(prevBulkInvoices.prevInvoices[bulkActionIndex].invoice.insurance);
      setAddOther(prevBulkInvoices.prevInvoices[bulkActionIndex].invoice.other);
      const onLoadAdditionalTotal = parseFloat(preformatFloat(prevBulkInvoices.prevInvoices[bulkActionIndex].invoice.tax)) + parseFloat(preformatFloat(prevBulkInvoices.prevInvoices[bulkActionIndex].invoice.shipping)) + parseFloat(preformatFloat(prevBulkInvoices.prevInvoices[bulkActionIndex].invoice.insurance)) + parseFloat(preformatFloat(prevBulkInvoices.prevInvoices[bulkActionIndex].invoice.other));
      setAdditionalCharges(onLoadAdditionalTotal.toLocaleString(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
      setUsPayrixUser(paymentProcessingEnabled && paymentProcessorType === 'p' && prevBulkInvoices.prevInvoices[bulkActionIndex].country === 'US');
    }
  };

  useEffect(() => {
    const currentTotal = parseFloat(addInsurance) + parseFloat(addOther) + parseFloat(addShipping) + parseFloat(addTax);
    setAdditionalCharges(currentTotal.toLocaleString(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
  }, [addInsurance, addOther, addShipping, addTax]);

  const checkForModalSwitch = (isPrev = false) => {
    let switchModals = false;
    let compareInvoice = null;
    if (isPrev && invoiceIndex > 0) {
      compareInvoice = invoices[invoiceIndex - 1];
    } else if (!isPrev && !(invoiceIndex === originalInvoices.length)) {
      compareInvoice = invoices[invoiceIndex + 1];
    }
    switchModals = (currentInvoice.showRestrictedModal !== compareInvoice.showRestrictedModal);
    return switchModals;
  };

  const handleOnSave = (goToNext) => async (e) => {
    try {
      setIsSavingCharges(true);
      await handleAddChargesByInvoice({ data: {
        shipping: addShipping,
        tax: addTax,
        other: addOther,
        insurance: addInsurance,
        invoiceID: currentInvoice.id,
        isLastInvoice: invoiceIndex === invoices.length - 1,
      } });
      setPrevBulkInvoices({ ...prevBulkInvoices,
        ...prevBulkInvoices.prevInvoices.push({ invoice: {
          shipping: addShipping,
          tax: addTax,
          other: addOther,
          insurance: addInsurance,
          invoiceID: currentInvoice.id,
        } }) });
      handleBulkActions(goToNext);
      if (goToNext) {
        if (checkForModalSwitch()) {
          onClose('invoice', true, invoiceIndex + 1);
        } else {
          setBulkActionIndex(bulkActionIndex + 1);
          setInvoiceIndex(invoiceIndex + 1);
        }
      } else {
        onClose();
      }
    } finally {
      setIsSavingCharges(false);
    }
  };

  const handlePreviousClick = () => {
    if (checkForModalSwitch(true)) {
      onClose('invoice', true, invoiceIndex - 1);
    } else {
      handlePreviousClickEvent();
      setBulkActionIndex(bulkActionIndex - 1);
      setInvoiceIndex((prev) => prev - 1);
      handleOnSave(true);
    }
  };

  const handleXCloseClick = () => {
    setInvoiceIndex(0);
    handleBulkActions(null);
  };

  const handleOnBlur = (e, field) => {
    const safeValue = parseFloat(Number(e.target.value) || 0).toFixed(2);
    switch (field) {
      case 'tax':
        setAddTax(safeValue);
        e.target.value = safeValue;
        break;
      case 'shipping':
        setAddShipping(safeValue);
        e.target.value = safeValue;
        break;
      case 'insurance':
        setAddInsurance(safeValue);
        e.target.value = safeValue;
        break;
      case 'other':
        setAddOther(safeValue);
        e.target.value = safeValue;
        break;
      default:
        break;
    }
  };

  useEffect(() => {
    if (typeof currentInvoice !== 'undefined') {
      setIsPaid(currentInvoice.allPaid);
      let isAddl = false;
      if (!hideAdditionalCode) {
        let paidByPP = false;
        if (currentInvoice.invoiceDetails.some(item => !item.restricted && (item.paymentMethod.trim() === 'paidByWePay' || item.paymentMethod.trim() === 'ACH'))) {
          paidByPP = true;
        }
        isAddl = ((currentInvoice.allPaid || currentInvoice.isPendingPayment || allVisibleArePaid(currentInvoice)) && paidByPP && paymentProcessingEnabled && paymentProcessorType === 'p');
      }
      setIsLotAdditionalVariant(isAddl);
      setIsLotVariant(!isAddl);
      setTextAdditionalPrefix(isAddl ? 'Add\'l ' : '');
    }
  }, [currentInvoice, invoiceIndex]);

  const renderForm = () => (
    <>
      <Table bordered className="edit-table mt-2">
        <thead>
          <tr>
            <th>Lot</th>
            <th>Hammer</th>
            <th>{textAdditionalPrefix}Tax</th>
            <th>{textAdditionalPrefix}S/H</th>
            <th>{textAdditionalPrefix}Insurance</th>
            <th>
              {textAdditionalPrefix}Other <FontAwesomeIcon id="additionalOtherToolTip" icon="question-circle" />
              <UncontrolledTooltip placement="right" target="additionalOtherToolTip">
                This field is for adding or discounting any additional fees.
                Please use a minus (-) to indicate a discount or adjustment.
              </UncontrolledTooltip>
            </th>
          </tr>
        </thead>
        <tbody>
          {currentInvoice.invoiceDetails.map((invoiceDetail) => (
            <tr key={`lotkey_${invoiceDetail.lotID}`}>
              <td id={`lot_${invoiceDetail.lotID}`} style={{ cursor: 'pointer' }} className="edit-cell-small">
                <a
                  href={invoiceDetail.lotURL}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {invoiceDetail.lotNum}{invoiceDetail.lotNumExt}
                  {invoiceDetail.restricted && paymentProcessingEnabled && ('*')}
                </a>
                <UncontrolledTooltip placement="right" target={`lot_${invoiceDetail.lotID}`}>
                  {invoiceDetail.lotTitle}
                </UncontrolledTooltip>
              </td>
              <td className="edit-cell-hammer">
                {currencySymbol}{invoiceDetail.priceInfo.hammer}
              </td>
              <td className="edit-cell-med">&nbsp;</td>
              <td className="edit-cell-med">&nbsp;</td>
              <td className="edit-cell-med">&nbsp;</td>
              <td className="edit-cell-med">&nbsp;</td>
            </tr>
          ))}
          {isLotAdditionalVariant && (
          <tr className="fees-row">
            <td colSpan={2} className="font-weight-bold mt-2">
              Paid Charges
            </td>
            <td className="edit-cell"> {currencySymbol} {currentInvoice.paidCharges.tax}</td>
            <td className="edit-cell"> {currencySymbol} {currentInvoice.paidCharges.shipping}</td>
            <td className="edit-cell"> {currencySymbol} {currentInvoice.paidCharges.insurance}</td>
            <td className="edit-cell"> {currencySymbol} {currentInvoice.paidCharges.other}</td>
          </tr>
          )}
          <tr className="fees-row">
            <td colSpan={2} className="font-weight-bold mt-2">
              {isLotAdditionalVariant ? 'Additional Charges' : 'Total Taxes & Fees'}
            </td>
            <td>
              <Input
                key={addTax}
                className={setFieldClasses(addTax, isPaid)}
                type="number"
                id="additionalTax"
                placeholder=""
                defaultValue={formatCurrencyForLocale(addTax || 0)}
                onFocus={(e) => clearInputField(e)}
                onBlur={(e) => handleOnBlur(e, 'tax')}
                disabled={usPayrixUser || (isLotVariant && isPaidOrPending) || (isLotAdditionalVariant && (currentInvoice.additionalPaid || currentInvoice.isPendingPayment || currentInvoice.additionalPending))}
              />
            </td>
            <td>
              <Input
                key={addShipping}
                className={setFieldClasses(addShipping, isPaid)}
                type="number"
                id="additionalShipping"
                placeholder=""
                defaultValue={formatCurrencyForLocale(addShipping || 0)}
                onFocus={(e) => clearInputField(e)}
                onBlur={(e) => handleOnBlur(e, 'shipping')}
                disabled={(isLotVariant && isPaidOrPending) || (isLotAdditionalVariant && (currentInvoice.additionalPaid || currentInvoice.isPendingPayment || currentInvoice.additionalPending))}
              />
            </td>
            <td>
              <Input
                key={addInsurance}
                className={setFieldClasses(addInsurance, isPaid)}
                type="number"
                id="additionalInsurance"
                placeholder=""
                defaultValue={formatCurrencyForLocale(addInsurance || 0)}
                onFocus={(e) => clearInputField(e)}
                onBlur={(e) => handleOnBlur(e, 'insurance')}
                disabled={(isLotVariant && isPaidOrPending) || (isLotAdditionalVariant && (currentInvoice.additionalPaid || currentInvoice.isPendingPayment || currentInvoice.additionalPending))}
              />
            </td>
            <td>
              <Input
                key={addOther}
                className={setFieldClasses(addOther, isPaid)}
                type="number"
                id="additionalOther"
                placeholder=""
                defaultValue={formatCurrencyForLocale(addOther || 0)}
                onFocus={(e) => clearInputField(e)}
                onBlur={(e) => handleOnBlur(e, 'other')}
                disabled={(isLotVariant && isPaidOrPending) || (isLotAdditionalVariant && (currentInvoice.additionalPaid || currentInvoice.isPendingPayment || currentInvoice.additionalPending))}
              />
            </td>
          </tr>
        </tbody>
      </Table>
    </>
  );
  const renderActions = () => {
    if (isBulkAction) {
      return (
        <Row className={`${blockEl}__actions`}>
          <Col sm="2">
            <p>
              {invoiceIndex + 1} of {invoices.length}
            </p>
          </Col>
          <Col sm="auto">
            {invoiceIndex > 0 && !hidePreviousLink && (
            <Button
              color="link"
              onClick={handlePreviousClick}
            >
              Previous invoice
            </Button>
            )}
            <Button
              color={invoiceIndex === invoices.length - 1 ? 'primary' : 'secondary'}
              onClick={handleOnSave(false)}
              disabled={isSavingCharges}
            >
              Save &amp; Close
            </Button>
            {invoiceIndex < invoices.length - 1 && (
              <Button
                color="primary"
                onClick={handleOnSave(true)}
                disabled={isSavingCharges}
              >
                Save &amp; Next
              </Button>
            )}
          </Col>
        </Row>
      );
    }

    return (
      <Button color="primary" onClick={handleOnSave(false)}>
        Save Changes
      </Button>
    );
  };
  if (!invoices.length || !currentInvoice) {
    return null;
  }

  return (
    <Modal
      isOpen={isOpen}
      className={blockEl}
      toggle={onClose}
      onClosed={handleXCloseClick}
    >
      <Modal.Header toggle={onClose}>Add/Edit Charges</Modal.Header>
      <Modal.Body>
        <Row className={`${blockEl}__details`}>
          <Col sm="8">
            <h4>{currentInvoice.name} <span className="small">(Paddle #{currentInvoice.paddleNumber})</span></h4>
            <p>
              Invoice #{currentInvoice.id} | {currentInvoice.location} | {formatChannelText(currentInvoice.channel)}
            </p>
          </Col>
          <Col sm="auto">
            <h4>{headingTotalText}</h4>
            <p>{currencySymbol}{additionalCharges}</p>
          </Col>
        </Row>
        {renderForm()}
      </Modal.Body>
      <Modal.Footer>{renderActions()}</Modal.Footer>
    </Modal>
  );
};
AddChargesModalByInvoice.propTypes = {
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
  variant: PropTypes.oneOf(['lot', 'lot-additional']),
};
AddChargesModalByInvoice.defaultProps = {
  invoices: [],
  isOpen: false,
  onClose: Function.prototype,
  variant: 'lot',
};

export default AddChargesModalByInvoice;
